import { Component, signal, inject, DestroyRef, type OnInit } from "@angular/core";
import { Router, RouterOutlet } from "@angular/router";
import { MsalService, MsalBroadcastService } from "@azure/msal-angular";
import { InteractionStatus } from "@azure/msal-browser";
import { filter } from "rxjs";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";

@Component({
  selector: "app-root",
  imports: [RouterOutlet],
  templateUrl: "./app.html",
  styleUrl: "./app.scss",
})
export class App implements OnInit {
  protected readonly title = signal("creator");

  private readonly authService = inject(MsalService);

  private readonly msalBroadcastService = inject(MsalBroadcastService);

  private readonly router = inject(Router);

  private readonly destroyRef = inject(DestroyRef);

  /**
   * Whether MSAL is idle. Tracked because the re-validation below must not fire
   * mid-redirect: during sign-in there is legitimately no active account yet.
   */
  private interactionIdle = false;

  ngOnInit(): void {
    // MSAL initialize() and handleRedirectPromise() run in an app initializer
    // (core/auth/msal-initializer.ts), so by the time this component exists the
    // instance is initialized and the active account may already be set — it is,
    // for a returning user or a completed redirect, and stays null when nobody
    // is signed in. The subscription below covers the accounts that arrive later.

    this.msalBroadcastService.inProgress$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((status: InteractionStatus) => {
        this.interactionIdle = status === InteractionStatus.None;
      });

    // Listen for interaction status changes
    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        // Check if we need to set an active account
        const accounts = this.authService.instance.getAllAccounts();
        if (accounts.length > 0 && !this.authService.instance.getActiveAccount()) {
          this.authService.instance.setActiveAccount(accounts[0]);
        }
      });

    this.guardAgainstRestoredSignedOutPage();
  }

  /**
   * Send a signed-out user to /login when the browser restores this SPA from
   * its back/forward cache.
   *
   * `logoutRedirect()` clears the MSAL cache *before* navigating to the
   * Microsoft sign-out page. Pressing Back from that page restores this app
   * from the bfcache: the JS heap comes back intact, so Angular does not
   * bootstrap again, the app initializer does not run again, and `authGuard`
   * never re-evaluates. The result is an authenticated-looking page for an
   * account that no longer exists — the header, tables and sidebar all still
   * rendered, with an empty user name and a "U" avatar because that is the
   * honest answer from `getActiveAccount()`.
   *
   * A bfcache restore fires `pageshow` with `persisted: true` and nothing else,
   * so this is the only hook available. Only that case is handled: on a normal
   * load `pageshow` can beat the app initializer, and the guards already cover
   * it correctly.
   */
  private guardAgainstRestoredSignedOutPage(): void {
    /**
     * `trustInteractionState` must be false for a restored page. `inProgress$`
     * is a BehaviorSubject, and a bfcache restore replays no events — so the
     * status stays frozen at whatever it was when the page unloaded. Logging
     * this path showed `interactionIdle: false` on every restore, because
     * logout had moved the status to `Logout` moments before navigating away.
     * Gating on it there suppressed the very redirect this exists to perform.
     */
    const redirectIfSignedOut = (trustInteractionState: boolean): void => {
      if (trustInteractionState && !this.interactionIdle) {
        return;
      }
      if (this.authService.instance.getActiveAccount()) {
        return;
      }
      void this.router.navigate(["/login"]);
    };

    // A bfcache restore fires only this, with persisted: true. Confirmed as the
    // path taken when returning from the Microsoft sign-out page via Back.
    const onPageShow = (event: PageTransitionEvent): void => {
      if (event.persisted) {
        redirectIfSignedOut(false);
      }
    };

    /*
     * Belt and braces. If the browser declines to bfcache the page it may still
     * come back without a fresh bootstrap, and that path fires no pageshow with
     * persisted: true. Re-checking whenever the tab becomes visible covers it,
     * and is harmless otherwise: a signed-out user belongs on /login regardless
     * of how they got here.
     */
    const onVisible = (): void => {
      if (document.visibilityState === "visible") {
        // A live page, so the interaction status is current and worth trusting:
        // during sign-in there is legitimately no account yet.
        redirectIfSignedOut(true);
      }
    };

    window.addEventListener("pageshow", onPageShow);
    document.addEventListener("visibilitychange", onVisible);
    this.destroyRef.onDestroy(() => {
      window.removeEventListener("pageshow", onPageShow);
      document.removeEventListener("visibilitychange", onVisible);
    });
  }

}
